import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { MapPin } from 'lucide-react';
import type { Tournament } from '@swiss-magic/shared-types';
import { isSafeHttpsLink, mapPlaceName, urlHost } from '@/lib/safeUrl';

// Tournament metadata grid extracted from TournamentPublicPage. Each
// row is rendered only when the corresponding tournament field is set
// — the page used to have one inline conditional per field. Pure
// presentational; receives a `tournament` and renders the public
// fields. No callbacks.

function systemLabel(system: string, t: TFunction): string {
  const key = `public_page.system_labels.${system}`;
  const translated = t(key);
  return translated === key ? system : translated;
}

function timeControlLabel(tc: string, t: TFunction): string {
  const slug = tc.toLowerCase().replace(/\s+/g, '_');
  const key = `public_page.time_control_labels.${slug}`;
  const translated = t(key);
  return translated === key ? tc : translated;
}

interface Props {
  tournament: Tournament;
}

export function TournamentDetailsGrid({ tournament }: Props) {
  const { t, i18n } = useTranslation('tournament');

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {tournament.gameName && tournament.gameName !== 'Chess' && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.game')}</p>
          <p data-testid="tournament-game">{tournament.gameName}</p>
        </div>
      )}
      {tournament.system && tournament.system !== 'swiss' && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.system')}</p>
          <p data-testid="tournament-system">{systemLabel(tournament.system, t)}</p>
        </div>
      )}
      {tournament.matchLength && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.match_length')}</p>
          <p data-testid="tournament-match-length">{t('public_page.fields.match_length_value', { length: tournament.matchLength })}</p>
        </div>
      )}
      {tournament.startDate && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.date')}</p>
          <p data-testid="tournament-date">
            {new Date(tournament.startDate + 'T00:00:00').toLocaleDateString(i18n.language, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
      )}
      {(tournament.location || isSafeHttpsLink(tournament.venueMapUrl)) && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.location')}</p>
          <p data-testid="tournament-location">
            {/* Story 084 — when a maps URL is set, the location row
                becomes a link with a MapPin icon so it reads as a
                map link rather than a plain link. Display name
                chain: explicit location text → name parsed from
                the URL → URL host. */}
            {isSafeHttpsLink(tournament.venueMapUrl) ? (
              <a
                href={tournament.venueMapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:text-primary/80"
              >
                <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                {tournament.location ||
                  mapPlaceName(tournament.venueMapUrl) ||
                  urlHost(tournament.venueMapUrl)}
              </a>
            ) : (
              tournament.location
            )}
          </p>
        </div>
      )}
      {isSafeHttpsLink(tournament.externalUrl) && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.external_url')}</p>
          <p>
            <a
              data-testid="tournament-external-url"
              href={tournament.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline-offset-4 hover:underline"
            >
              {tournament.externalUrlLabel || urlHost(tournament.externalUrl)}
            </a>
          </p>
        </div>
      )}
      {tournament.timeControl && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.time_control')}</p>
          <p data-testid="tournament-time-control">{timeControlLabel(tournament.timeControl, t)}</p>
        </div>
      )}
      {tournament.rounds && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.rounds')}</p>
          <p data-testid="tournament-rounds">{tournament.rounds}</p>
        </div>
      )}
      {tournament.maxPlayers && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.max_players')}</p>
          <p data-testid="tournament-max-players">{tournament.maxPlayers}</p>
        </div>
      )}
      {tournament.entryFee && (
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t('public_page.fields.entry_fee')}</p>
          <p data-testid="tournament-entry-fee">{tournament.entryFee}</p>
        </div>
      )}
    </div>
  );
}
